import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { LucideIcon } from "../components/LucideIcon";
import { useContactData } from "../hooks/useContactData";

export const Security = () => {
  const contactData = useContactData();
  const highlights = contactData.securityHighlights || [];

  return (
    <>
      <SEO
        title="Security & Trust"
        description="Bank-grade encryption, role-based access, tenant isolation, and backups — how OOMS protects CA, CS, and legal client data."
        canonicalUrl={`${contactData.websiteUrl}/security`}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Security", item: "/security" },
        ]}
      />

      <section className="pt-20 pb-16 text-center px-4 max-w-4xl mx-auto space-y-5">
        <span className="text-xs font-black uppercase tracking-[0.2em] text-primary-600 dark:text-primary-400 font-heading">
          Trust Center
        </span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl text-foreground leading-[0.95] tracking-tighter">
          Security Built for Professional Practices
        </h1>
        <p className="text-text-sub text-lg leading-relaxed font-medium">
          Taxpayer records, petitions, and working papers deserve controls that
          match your professional duty of care — not a generic shared drive.
        </p>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {highlights.map((item, idx) => (
            <Reveal key={item.title} delay={idx * 0.05}>
              <div className="h-full rounded-[32px] border border-border bg-surface p-8 shadow-xl shadow-foreground/5">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100 text-primary-700 dark:bg-primary-950/40 dark:text-primary-400">
                  <LucideIcon name={item.iconName} className="h-6 w-6" />
                </div>
                <h2 className="font-heading font-black text-xl text-foreground mb-3">
                  {item.title}
                </h2>
                <p className="text-sm text-text-sub leading-relaxed font-medium">
                  {item.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted border-y border-border">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="space-y-5">
              <h2 className="font-heading font-black text-3xl tracking-tighter text-foreground">
                What partners ask us most
              </h2>
              <ul className="space-y-4">
                {[
                  "Who can open VIP client folders?",
                  "Are sessions controllable across devices?",
                  "Can we export data if we leave?",
                  "Where does data live and how is it backed up?",
                ].map((q) => (
                  <li
                    key={q}
                    className="flex items-start gap-3 text-foreground font-bold"
                  >
                    <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[32px] border border-border bg-surface p-8 space-y-4">
              <p className="text-text-sub font-medium leading-relaxed">
                Answers live in our FAQ and business policies. For a deep dive
                with your IT or compliance partner, schedule a security-focused
                demo.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/faq"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary-600 px-6 py-3 font-heading font-black text-white hover:bg-primary-700"
                >
                  Read Security FAQs <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/business-policy"
                  className="inline-flex items-center justify-center rounded-2xl border border-border px-6 py-3 font-heading font-black text-foreground"
                >
                  Business Policy
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 px-4 max-w-7xl mx-auto">
        <Reveal className="text-center mb-10 space-y-3">
          <h2 className="font-heading font-black text-3xl tracking-tighter text-foreground">
            Trust indicators
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {contactData.trustIndicators.map((ind, i) => (
            <Reveal key={ind.title} delay={i * 0.05}>
              <div className="rounded-[28px] border border-border bg-surface p-6 text-center">
                <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-700 dark:bg-primary-950/40 dark:text-primary-400">
                  <LucideIcon name={ind.iconName} className="h-5 w-5" />
                </div>
                <h3 className="font-heading font-black text-sm text-foreground mb-2">
                  {ind.title}
                </h3>
                <p className="text-xs text-text-sub font-medium leading-relaxed">
                  {ind.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
};

export default Security;
